// Turkish attaches suffixes to the SPOKEN form of a number, not its digits:
// %34 is "yüzde otuz dört", so "aktifin %34'ü" (dördü), while %17 ends in
// "yedi" and takes "'si". A fixed "'i" in the bundle is right for a third of
// the values and wrong for the rest, and ICU select cannot express it either,
// since the branch is a function of the number's last spoken word. i18next has
// no plural-style hook for this, so it is a named formatter: a bundle string
// writes `%{{percent}}'{{percent, possessive_tr}}` and the suffix follows the
// number wherever the string is reused.

const UNITS = ["sıfır", "bir", "iki", "üç", "dört", "beş", "altı", "yedi", "sekiz", "dokuz"];
const TENS = ["", "on", "yirmi", "otuz", "kırk", "elli", "altmış", "yetmiş", "seksen", "doksan"];
// Index = digit-group (thousands, millions, …): what a number ending in a
// round thousand reads as its last word, e.g. 5000 "beş bin" → "bin".
const SCALES = ["", "bin", "milyon", "milyar", "trilyon"];

const VOWELS = "aeıioöuü";

const HIGH_VOWEL_AFTER: Readonly<Record<string, string>> = {
  a: "ı",
  ı: "ı",
  e: "i",
  i: "i",
  o: "u",
  u: "u",
  ö: "ü",
  ü: "ü",
};

/**
 * The word a number is read out as LAST, which is all that decides the suffix.
 * Only the last non-zero digit matters: 40 → "kırk", 100 → "yüz", 1200 → "yüz",
 * 5000 → "bin".
 */
function lastSpokenWord(digits: string): string {
  const trimmed = digits.replace(/^0+(?=\d)/, "");
  if (/^0+$/.test(trimmed) || trimmed === "") return UNITS[0] as string;
  const reversed = [...trimmed].reverse();
  const position = reversed.findIndex((d) => d !== "0");
  const digit = Number(reversed[position]);
  if (position === 0) return UNITS[digit] as string;
  if (position === 1) return TENS[digit] as string;
  if (position === 2) return "yüz";
  return SCALES[Math.min(Math.floor(position / 3), SCALES.length - 1)] as string;
}

/**
 * The third-person possessive (-(s)I) for a non-negative integer, without the
 * apostrophe: 34 → "ü" (dördü), 17 → "si" (yedisi), 25 → "i" (yirmi beşi),
 * 100 → "ü" (yüzü). Grouping separators are ignored (the input is often an
 * already-formatted "1.000"); a string with no digits yields "".
 */
export function turkishPossessiveSuffix(value: number | string): string {
  const digits = String(value).replace(/\D/g, "");
  if (digits === "") return "";
  const word = lastSpokenWord(digits);
  const last = word[word.length - 1] as string;
  const lastVowel = [...word].reverse().find((c) => VOWELS.includes(c)) as string;
  const vowel = HIGH_VOWEL_AFTER[lastVowel] as string;
  return VOWELS.includes(last) ? `s${vowel}` : vowel;
}
